import React, { useEffect, useState } from 'react';
import './DiagnosticPopup.css';

// Octave-folding frequency to CSS color (mirrors iOS TorusColors.swift)
function frequencyToCSS(freq, resonance = 0) {
  if (!freq || freq <= 0) return 'rgb(40, 40, 40)';
  const hue = ((Math.log2(freq / 80)) % 1.0 + 1.0) % 1.0;
  const sat = 85 + Math.min(resonance / 100, 1.0) * 15;
  return `hsl(${Math.round(hue * 360)}, ${Math.round(sat)}%, 60%)`;
}

const GlobalDiagnosticPopup = ({ data, onClose, diagnosticData = null, onCaseClick = null, onHistorySelect = null }) => {
  if (!data) return null;

  const [diagnosticHistory, setDiagnosticHistory] = useState([]);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(-1);

  useEffect(() => {
    const history = JSON.parse(localStorage.getItem('diagnostic_history') || '[]');
    const sortedHistory = history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setDiagnosticHistory(sortedHistory);
  }, []);

  // Extract metrics from local analysis data
  const getLocalMetrics = () => {
    if (!diagnosticData) return null;

    // Find metrics_42 in diagnosticData (various shapes)
    let metrics = null;
    let features = null;
    let globalScore = 0;

    if (Array.isArray(diagnosticData)) {
      // Array of entries — find one with metrics_42
      const entry = diagnosticData.find(d => d && d.metrics_42);
      if (entry) {
        metrics = entry.metrics_42;
        features = entry.features;
        globalScore = entry.globalScore || 0;
      }
    } else if (diagnosticData.metrics_42) {
      metrics = diagnosticData.metrics_42;
      features = diagnosticData.features;
      globalScore = diagnosticData.globalScore || 0;
    }

    // Fallback: compute from diagnosticData array entries that have analysis
    if (!metrics && Array.isArray(diagnosticData)) {
      const completed = diagnosticData.filter(d => d && d.score > 0 && d.progress === 1);
      if (completed.length > 0) {
        globalScore = Math.round(completed.reduce((s, d) => s + (d.score || 0) * 100, 0) / completed.length);
      }
    }

    return { metrics, features, globalScore };
  };

  const localData = getLocalMetrics();
  const globalScore = data.globalScore || localData?.globalScore || 0;
  const features = localData?.features || {};
  const metrics = localData?.metrics;

  const popupStyle = {
    position: 'fixed',
    top: '50%',
    left: '20px',
    transform: 'translateY(-50%)',
    maxHeight: '80vh',
    overflowY: 'auto',
    width: '350px'
  };

  return (
    <div className="diagnostic-popup" style={{
      ...popupStyle,
      backgroundColor: 'rgba(20, 20, 30, 0.95)',
      color: 'white',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)'
    }}>
      <button
        className="popup-close"
        onClick={onClose}
        style={{ color: 'white', backgroundColor: 'transparent', border: 'none', fontSize: '18px' }}
      >
        &times;
      </button>

      {/* Header with global score */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', color: 'white' }}>Dashboard Vocal</h3>
        <span style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: globalScore >= 80 ? '#4caf50' : globalScore >= 60 ? '#ff9800' : globalScore > 0 ? '#f44336' : '#666'
        }}>
          {globalScore > 0 ? `${globalScore}%` : '-'}
        </span>
      </div>

      {/* 3 Metric Cards */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
        <MetricCard
          label="F0 moyenne"
          value={features.f0_mean ? `${features.f0_mean} Hz` : '-'}
          color="#4facfe"
        />
        <MetricCard
          label="Volume"
          value={features.volume_mean ? `${features.volume_mean}%` : '-'}
          color="#43e97b"
        />
        <MetricCard
          label="Resonance"
          value={features.resonance_mean ? `${features.resonance_mean}%` : '-'}
          color="#fa709a"
        />
      </div>

      {/* Mini torus: 42 colored segments */}
      {metrics && (
        <div style={{
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '8px',
          padding: '10px',
          marginBottom: '14px'
        }}>
          <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>42 segments vocaux</div>
          <div style={{ display: 'flex', gap: '1px', height: '24px', borderRadius: '4px', overflow: 'hidden' }}>
            {metrics.map((m, i) => (
              <div
                key={i}
                onClick={() => onCaseClick && onCaseClick(i)}
                title={`${m.value} (${m.description})`}
                style={{
                  flex: 1,
                  backgroundColor: m.isActive ? frequencyToCSS(m.frequency, m.resonance) : 'rgba(255,255,255,0.08)',
                  cursor: 'pointer',
                  transition: 'transform 0.1s',
                  borderRadius: i === 0 ? '3px 0 0 3px' : i === 41 ? '0 3px 3px 0' : '0'
                }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#666', marginTop: '4px' }}>
            <span>0s</span>
            <span>{features.duration ? `${features.duration}s` : ''}</span>
          </div>
        </div>
      )}

      {/* Duration & active segments info */}
      {features.duration && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-around',
          marginBottom: '14px',
          fontSize: '11px',
          color: '#aaa'
        }}>
          <span>Duree: {features.duration}s</span>
          <span>Actifs: {features.active_segments || 0}/{features.total_segments || 42}</span>
        </div>
      )}

      {/* Diagnostic History */}
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        padding: '10px',
        borderRadius: '6px',
        marginBottom: '10px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        maxHeight: '220px',
        overflowY: 'auto'
      }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#aaa', fontWeight: 'normal' }}>
          Historique ({diagnosticHistory.length})
        </h4>

        {diagnosticHistory.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.4)', fontSize: '12px', padding: '16px' }}>
            Aucun diagnostic
          </div>
        ) : (
          diagnosticHistory.map((diagnostic, index) => {
            const date = new Date(diagnostic.timestamp);
            const dateStr = date.toLocaleDateString('fr-FR');
            const timeStr = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

            return (
              <div
                key={index}
                onClick={() => {
                  setSelectedHistoryIndex(index);
                  if (onHistorySelect && diagnostic.diagnosticData) {
                    onHistorySelect(diagnostic.diagnosticData, diagnostic);
                  }
                }}
                style={{
                  backgroundColor: selectedHistoryIndex === index ? 'rgba(74, 172, 254, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                  border: selectedHistoryIndex === index ? '1px solid rgba(74, 172, 254, 0.4)' : '1px solid transparent',
                  borderRadius: '6px',
                  padding: '8px 10px',
                  marginBottom: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span style={{ fontSize: '12px', color: '#ccc' }}>
                  {dateStr} {timeStr}
                </span>
                <span style={{
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: diagnostic.globalScore >= 80 ? '#4caf50' : diagnostic.globalScore >= 60 ? '#ff9800' : '#f44336'
                }}>
                  {diagnostic.globalScore || 0}%
                </span>
              </div>
            );
          })
        )}
      </div>

      {/* New diagnostic button */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
        <button
          onClick={() => {
            onClose();
            if (window.toggleVoiceRecording) {
              window.toggleVoiceRecording();
            }
          }}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '6px',
            padding: '8px 20px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          Nouveau Diagnostic
        </button>
      </div>
    </div>
  );
};

// Small metric card component
const MetricCard = ({ label, value, color }) => (
  <div style={{
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '8px',
    padding: '10px 8px',
    textAlign: 'center',
    borderTop: `2px solid ${color}`
  }}>
    <div style={{ fontSize: '10px', color: '#888', marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '14px', fontWeight: 'bold', color }}>{value}</div>
  </div>
);

export default GlobalDiagnosticPopup;
